import { call, put, select, takeLatest, fork, all } from 'redux-saga/effects';
import axios from 'axios';
import { SagaIterator } from 'redux-saga';

import { updateQueryString } from 'services/router/actions';
// @ts-ignore
import { selectCurrentOrganizationId } from 'services/currentOrganizationId/selectors';
// @ts-ignore
import { idTokenSelector } from 'services/auth/selectors';
import { ERROR_MODAL_QUERY_STRING_PARAM } from 'components/ErrorModal/constants';
import { setSubmitHasFailed } from 'services/virtualizedTable/actions';

import {
    RequestedCreateEquipmentAction,
    RequestedUpdateEquipmentAction,
    RequestedDeleteEquipmentAction
} from './types';
import {
    succeededCreateEquipment,
    failedCreateEquipment,
    succeededUpdateEquipment,
    failedUpdateEquipment,
    succeededDeleteEquipment,
    failedDeleteEquipment,
    setSingleEquipment
} from './actions';
import {
    EQUIPMENT_CREATE_REQUESTED,
    EQUIPMENT_UPDATE_REQUESTED,
    EQUIPMENT_DELETE_REQUESTED
} from './constants';

// Environment
const { REACT_APP_API_URL } = process.env;

export function* postEquipment(
    action: RequestedCreateEquipmentAction
): SagaIterator {
    const [idToken, organizationId] = yield all([
        select(idTokenSelector),
        select(selectCurrentOrganizationId)
    ]);
    const url = `${REACT_APP_API_URL}/v1/Equipment`;

    try {
        const response = yield call(axios, url, {
            method: 'post',
            headers: {
                Authorization: idToken
            },
            data: {
                organizationId,
                ...action.payload
            }
        });
        yield all([
            put(succeededCreateEquipment(response)),
            put(setSingleEquipment(response.data))
        ]);
    } catch (error) {
        yield all([
            put(failedCreateEquipment(error)),
            put(setSubmitHasFailed(true)),
            put(
                updateQueryString({
                    [ERROR_MODAL_QUERY_STRING_PARAM]: 'EQUIPMENT_CREATE'
                })
            )
        ]);
    }
}

export function* patchEquipment(
    action: RequestedUpdateEquipmentAction
): SagaIterator {
    const idToken = yield select(idTokenSelector);

    const {
        payload: { equipmentId, equipment }
    } = action;

    try {
        const url = `${REACT_APP_API_URL}/v1/Equipment/${equipmentId}`;

        const response = yield call(axios, url, {
            method: 'patch',
            headers: {
                Authorization: idToken
            },
            data: equipment
        });
        yield all([
            put(succeededUpdateEquipment(response)),
            put(setSingleEquipment(response.data))
        ]);
    } catch (error) {
        yield all([
            put(failedUpdateEquipment(error)),
            put(setSubmitHasFailed(true)),
            put(
                updateQueryString({
                    [ERROR_MODAL_QUERY_STRING_PARAM]: 'EQUIPMENT_UPDATE'
                })
            )
        ]);
    }
}

export function* deleteEquipment(
    action: RequestedDeleteEquipmentAction
): SagaIterator {
    const idToken = yield select(idTokenSelector);
    const { equipmentId } = action.payload;

    try {
        const url = `${REACT_APP_API_URL}/v1/Equipment/${equipmentId}`;
        const response = yield call(axios, url, {
            method: 'delete',
            headers: {
                Authorization: idToken
            }
        });
        const res = yield call(axios, url, {
            method: 'get',
            headers: {
                Authorization: idToken
            }
        });
        yield all([
            put(succeededDeleteEquipment(response)),
            put(setSingleEquipment(res.data))
        ]);
    } catch (error) {
        yield all([
            put(failedDeleteEquipment(error)),
            put(setSubmitHasFailed(true)),
            put(
                updateQueryString({
                    [ERROR_MODAL_QUERY_STRING_PARAM]: 'EQUIPMENT_DELETE'
                })
            )
        ]);
    }
}

export function* watchCreateEquipment(): SagaIterator {
    yield takeLatest(EQUIPMENT_CREATE_REQUESTED, postEquipment);
}

export function* watchUpdateEquipment(): SagaIterator {
    yield takeLatest(EQUIPMENT_UPDATE_REQUESTED, patchEquipment);
}

export function* watchDeleteEquipment(): SagaIterator {
    yield takeLatest(EQUIPMENT_DELETE_REQUESTED, deleteEquipment);
}

export default function* equipmentSetsSagas(): SagaIterator {
    yield all([
        fork(watchCreateEquipment),
        fork(watchUpdateEquipment),
        fork(watchDeleteEquipment)
    ]);
}
